// Global capture of the PWA `beforeinstallprompt` event.
//
// The event fires exactly once per page load (when Chrome decides the app
// meets installability criteria). If we miss it, we can't trigger a native
// install dialog later. So we set up the listener at module-load time
// (before React mounts) and stash the event on a singleton.
import { useEffect, useState } from "react";

let deferredPrompt = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => {
    try {
      fn(!!deferredPrompt);
    } catch (_) {
      /* ignore */
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar from auto-appearing — we'll trigger the
    // native dialog programmatically when the user taps our Install button.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function isInstallableNow() {
  return !!deferredPrompt;
}

export function subscribeInstallable(fn) {
  subscribers.add(fn);
  // Push the current state immediately
  try {
    fn(!!deferredPrompt);
  } catch (_) {
    /* ignore */
  }
  return () => subscribers.delete(fn);
}

/**
 * Programmatically open the native install dialog. Returns one of:
 *   - "accepted" — user installed
 *   - "dismissed" — user said no
 *   - "unavailable" — no captured prompt (iOS, missing SW, already installed,
 *     unsupported browser). Caller should fall back to manual instructions.
 */
export async function promptInstall() {
  const evt = deferredPrompt;
  if (!evt) return "unavailable";
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    // Per spec the event can only be used once.
    deferredPrompt = null;
    notify();
    return choice && choice.outcome === "accepted" ? "accepted" : "dismissed";
  } catch (_) {
    deferredPrompt = null;
    notify();
    return "dismissed";
  }
}

/** React hook returning whether a native install dialog is currently
 * available to be triggered programmatically. */
export function useInstallable() {
  const [available, setAvailable] = useState(isInstallableNow());
  useEffect(() => subscribeInstallable(setAvailable), []);
  return available;
}
